import { useCallback } from "react";
import { useConnectModal } from "thirdweb/react";
import {
    arbitrum,
    avalanche,
    base,
    baseSepolia,
    bsc,
    ethereum,
    optimism,
    polygon,
} from "thirdweb/chains";
import { createWallet } from "thirdweb/wallets";
import { ThirdWebClient } from "../contexts/thirdWebClient";
import { useTheme } from "../contexts/ThemeContext";
import { useIsMobile } from "./useMediaQuery";

const WALLET_IDS = [
    "io.metamask",
    "com.coinbase.wallet",
    "me.rainbow",
    "io.rabby",
    "app.phantom",
    "com.trustwallet.app",
    "walletConnect",
];

/* One set of wallet instances for the whole app: the header button and any
   in-flow prompt must offer the same list, and thirdweb keeps state on these
   objects, so they are built once rather than per render. */
let wallets;
export const getWallets = () => {
    if (!wallets) wallets = WALLET_IDS.map(createWallet);
    return wallets;
};

/* The networks the bridge works on. WalletConnect sessions only cover the
   chains asked for when they are opened, and a request on any other one
   fails with "Missing or invalid. request() chainId: eip155:<id>" — so the
   bridge, which leaves the wallet's network alone, still has to list them.
   Base comes first: the USDXSMART sale and its fee are sent there. */
export const BRIDGE_CHAINS = [
    base,
    ethereum,
    arbitrum,
    optimism,
    polygon,
    bsc,
    avalanche,
    baseSepolia,
];

export const CONNECT_MODAL_LOGO = "/USDX.png";

export const connectModalCopy = {
    title: "Sign in with USDX",
    titleIcon: CONNECT_MODAL_LOGO,
    welcomeScreen: {
        title: "Your gateway to the decentralized world",
        subtitle: "Connect a wallet to get started",
        img: { src: CONNECT_MODAL_LOGO, height: 150, width: 150 },
    },
};

/**
 * Opens the same wallet modal the header uses, from anywhere in a flow.
 *
 * Every step that needs an account can then ask for one in place instead of
 * dead-ending on a disabled button that says "Connect Wallet".
 *
 * `chain` is the network the wallet is asked to switch to on connect —
 * Base Sepolia, where the DEX is deployed. Pass `null` to leave the wallet on
 * whatever network it is already on (the bridge, which works across chains
 * and asks for the one it needs itself).
 */
export function useConnectWallet({ chain = baseSepolia } = {}) {
    const { connect, isConnecting } = useConnectModal();
    const { theme } = useTheme();
    const isMobile = useIsMobile();

    const openConnect = useCallback(() => {
        connect({
            client: ThirdWebClient,
            wallets: getWallets(),
            ...(chain ? { chain } : { chains: BRIDGE_CHAINS }),
            /* "wide" puts the welcome panel beside the wallet list, which does
               not fit a phone. */
            size: isMobile ? "compact" : "wide",
            theme,
            ...connectModalCopy,
        }).catch(() => {
            /* Rejects when the modal is dismissed — not an error worth showing. */
        });
    }, [connect, isMobile, theme, chain]);

    return { openConnect, isConnecting };
}
